import { DeviceStatus } from '@lmc/ts-common'
import { EmptyState, NoDataIcon, Tabs } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

// Sentinel tab keys that do not map onto a DeviceStatus.
export const ALL_DEVICES_TAB = 'devices'
export const SIGNS_TAB = 'signs'

export type ActiveTab = DeviceStatus | typeof ALL_DEVICES_TAB | typeof SIGNS_TAB

type StatusSwitchProps = {
  hasDeviceAccessControl?: boolean
  isLoading: boolean
  hasSpecificStatusDevices: boolean
  activeTab: ActiveTab
  onChangeTab: (tab: ActiveTab) => void
  testId?: string
}

const StatusSwitch: FC<StatusSwitchProps> = ({
  hasDeviceAccessControl,
  isLoading,
  hasSpecificStatusDevices,
  activeTab,
  onChangeTab,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const emptyStateFor = (title: string, description?: string) =>
    !isLoading && !hasSpecificStatusDevices ? (
      <EmptyState
        title={title}
        description={description ? <Text>{description}</Text> : undefined}
        icon={<NoDataIcon />}
      />
    ) : undefined

  const enabledItems = [
    {
      key: DeviceStatus.pending,
      label: t(dacConstants.PENDING_DEVICES),
      children: emptyStateFor(t(dacConstants.DEVICES_PENDING))
    },
    {
      key: DeviceStatus.approved,
      label: t(dacConstants.APPROVED_DEVICES),
      children: emptyStateFor(
        t(dacConstants.NO_DEVICES_APPROVED),
        t(dacConstants.DEVICES_APPROVE)
      )
    },
    {
      key: DeviceStatus.blocked,
      label: t(dacConstants.BLOCKED_DEVICES),
      children: emptyStateFor(
        t(dacConstants.NO_DEVICES_BLOCKED),
        t(dacConstants.DEVICES_BLOCKED)
      )
    },
    {
      key: SIGNS_TAB,
      label: t(dacConstants.DEVICE_SIGN_INS),
      children: undefined
    }
  ]

  const disabledItems = [
    {
      key: ALL_DEVICES_TAB,
      label: t(dacConstants.DEVICES),
      children: undefined
    },
    {
      key: SIGNS_TAB,
      label: t(dacConstants.DEVICE_SIGN_INS),
      children: undefined
    }
  ]

  return (
    <Tabs
      data-testid={testId}
      items={hasDeviceAccessControl ? enabledItems : disabledItems}
      activeKey={activeTab}
      onChange={(activeKey) => onChangeTab(activeKey as ActiveTab)}
    />
  )
}

export default StatusSwitch
